/**
 * <DynamicReportPage /> — resolves `/reports/:domain/:reportId` to
 * a catalog manifest, builds a ReportConfig from per-domain
 * defaults, and renders via <ConfigurableReportPage>.
 *
 * The per-report column / KPI / chart customization happens via
 * `reportConfigsByDomain` overrides; un-customized reports use
 * the generic defaults (whatever the backend aggregator returns
 * goes straight into the table).
 */

import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  ConfigurableReportPage,
  type ReportConfig,
} from "../../components/ui/ConfigurableReportPage";
import { EmptyState } from "../../components/ui/EmptyState";
import { PageHeader } from "../../components/ui/PageHeader";
import { apiBaseUrl } from "../../lib/api";
import {
  type ReportDomain,
  reportById,
} from "../../lib/reports/catalog";

export function DynamicReportPage() {
  const { domain, reportId } = useParams<{
    domain: string;
    reportId: string;
  }>();
  const { t } = useTranslation();

  if (!domain || !reportId) {
    return <NotFound />;
  }

  const manifest = reportById(reportId);
  if (!manifest || manifest.domain !== domain) {
    return <NotFound />;
  }

  // Was `apiBase = ""` which sent the fetch SAME-ORIGIN to the
  // Cloudflare console worker instead of the Railway backend. The
  // worker's SPA fallback served index.html for /api/*, JSON parse
  // returned 0 rows, every report rendered empty. `apiBaseUrl` is
  // the same export the rest of the app uses via `request()`.
  const endpoint = `${apiBaseUrl}/api/reports/catalog/${manifest.domain}/${manifest.id}`;

  const config: ReportConfig = {
    id: manifest.id,
    title: t(manifest.titleKey, manifest.titleKey),
    description: t(manifest.descriptionKey, manifest.descriptionKey),
    domain: manifest.domain as ReportDomain,
    agent: manifest.agent,
    aiTier: manifest.aiTier,
    category: manifest.category,
    article: manifest.article,
    endpoint,
    // Generic default columns — the backend returns whatever shape
    // it has. The DataTable handles arbitrary keys; per-report
    // overrides can replace this with typed columns later.
    columns: [],
    factsBuilder: (rows) => ({ rows: rows.length }),
  };

  return <ConfigurableReportPage config={inferColumnsFromFirstRow(config)} />;
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t("reports.not_found_title", "Report not found")} />
      <EmptyState
        headline={t("reports.not_found_title", "Report not found")}
        description={t(
          "reports.not_found_desc",
          "This report isn't in the catalog. Open the Reports index to browse.",
        )}
      />
    </div>
  );
}

function inferColumnsFromFirstRow(config: ReportConfig): ReportConfig {
  // Columns are inferred at runtime inside ConfigurableReportPage
  // when this list is empty — keeps the dynamic page generic.
  return config;
}
